import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Play, Pause, SkipBack, LogOut, Upload, Loader2, Disc } from 'lucide-react';
import WaveSurfer from 'wavesurfer.js';
import JogWheel from './JogWheel';

// Volume knob with snap positions
const VolumeKnob = ({ value, onChange, deckColor }) => {
  const [isDragging, setIsDragging] = useState(false);
  const knobRef = useRef(null);
  
  // Snap positions: 0%, 25%, 50%, 75%, 100%
  const snapPositions = [0, 0.25, 0.5, 0.75, 1];
  const snapThreshold = 0.05;
  
  const snapToNearest = useCallback((val) => {
    for (const snap of snapPositions) {
      if (Math.abs(val - snap) < snapThreshold) {
        return snap;
      }
    }
    return val;
  }, []);
  
  const handleMouseDown = (e) => {
    setIsDragging(true);
    e.preventDefault();
  };
  
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e) => {
      if (!knobRef.current) return;
      const rect = knobRef.current.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      const centerX = rect.left + rect.width / 2;
      const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX);
      
      // Convert angle to value (0-1), mapping -135deg to 135deg
      let normalized = (angle + Math.PI) / (2 * Math.PI);
      normalized = (normalized + 0.375) % 1; // Offset to start from bottom-left
      const newValue = Math.max(0, Math.min(1, normalized * 1.5 - 0.25));
      onChange(snapToNearest(newValue));
    };
    
    const handleMouseUp = () => setIsDragging(false);
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onChange, snapToNearest]);
  
  // Rotation: -135deg (0%) to +135deg (100%)
  const rotation = -135 + (value * 270);
  
  return (
    <div className="volume-knob-container" ref={knobRef} onMouseDown={handleMouseDown}>
      <svg width="38" height="38" viewBox="0 0 50 50" className="volume-knob-svg">
        <defs>
          <linearGradient id="volKnobGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3a3a4a" />
            <stop offset="50%" stopColor="#2a2a3a" />
            <stop offset="100%" stopColor="#1a1a2a" />
          </linearGradient>
        </defs>
        {/* Outer ring */}
        <circle cx="25" cy="25" r="23" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="2" />
        {/* Knob body */}
        <circle cx="25" cy="25" r="18" fill="url(#volKnobGrad)" />
        {/* Indicator line */}
        <line 
          x1="25" y1="25" x2="25" y2="10" 
          stroke={deckColor} strokeWidth="2" strokeLinecap="round"
          transform={`rotate(${rotation}, 25, 25)`}
        />
        {/* Center dot */}
        <circle cx="25" cy="25" r="3" fill={deckColor} opacity="0.6" />
      </svg>
      <span className="vol-knob-label" style={{ color: deckColor }}>VOL</span>
      <span className="vol-knob-value">{Math.round(value * 100)}%</span>
    </div>
  );
};

// Pitch slider with center snap - DJ Standard: UP = slower (-), DOWN = faster (+)
const PitchSlider = ({ value, onChange, deckColor }) => {
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);
  
  const handleMouseDown = (e) => {
    setIsDragging(true);
    updateValue(e);
    e.preventDefault();
  };
  
  const updateValue = useCallback((e) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    // DJ Standard: top = -8 (slower), bottom = +8 (faster)
    const percent = (e.clientY - rect.top) / rect.height;
    let newValue = (percent * 16) - 8; // -8 to +8
    newValue = Math.max(-8, Math.min(8, newValue));
    
    // Center snap at 0
    if (Math.abs(newValue) < 0.5) {
      newValue = 0;
    }
    
    onChange(newValue);
  }, [onChange]);
  
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e) => updateValue(e);
    const handleMouseUp = () => setIsDragging(false);
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, updateValue]);
  
  // Convert value (-8 to +8) to position (0 to 100%) - inverted for DJ standard
  const position = ((value + 8) / 16) * 100;
  
  return (
    <div className="pitch-slider-container" ref={sliderRef} onMouseDown={handleMouseDown}>
      <div className="pitch-track">
        <div className="pitch-markers">
          <span>-8</span>
          <span>0</span>
          <span>+8</span>
        </div>
        <div className="pitch-rail">
          <div className="pitch-center-line" />
          <div 
            className="pitch-thumb" 
            style={{ top: `${position}%`, borderColor: deckColor }}
          />
        </div>
      </div>
      <span className="pitch-label">PITCH</span>
      <span className="pitch-value" style={{ color: value === 0 ? 'rgba(255,255,255,0.5)' : deckColor }}>
        {value > 0 ? '+' : ''}{value.toFixed(1)}%
      </span>
    </div>
  );
};

const DeckCore = ({ 
  deck, 
  state, 
  mode,
  onPlay, 
  onVolumeChange, 
  onPitchChange,
  onCuePoint,
  onPositionUpdate,
  onLoadTrack,
  onEject,
  onSeek, // New: seek callback
  audioContextStarted,
  masterVolume = 1,
  crossfaderValue = 0.5
}) => {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const audioRef = useRef(null); // Audio element reference for CORS handling
  const [isWaveformReady, setIsWaveformReady] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [cueActive, setCueActive] = useState(false);
  
  // Calculate effective volume based on deck volume, master volume, and crossfader
  const calculateEffectiveVolume = useCallback(() => {
    // Crossfader calculation: Deck A = cos curve, Deck B = sin curve
    let crossfaderMultiplier;
    if (deck === 'A') {
      crossfaderMultiplier = Math.cos(crossfaderValue * Math.PI / 2);
    } else {
      crossfaderMultiplier = Math.sin(crossfaderValue * Math.PI / 2);
    }
    return state.volume * masterVolume * crossfaderMultiplier;
  }, [deck, state.volume, masterVolume, crossfaderValue]);
  
  // Initialize WaveSurfer - only when we have a track and the container is ready
  useEffect(() => {
    // Wait for track to load so the container is rendered
    if (!waveformRef.current || !audioContextStarted || !state.track) return;
    
    // Destroy existing instance if any
    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
      wavesurferRef.current = null;
    }
    
    // If we have an audio URL, create WaveSurfer with a pre-configured Audio element
    // This is the WaveSurfer v7 way to handle cross-origin audio
    if (state.audioUrl) {
      console.log(`Deck ${deck}: Creating WaveSurfer with audio URL:`, state.audioUrl.substring(0, 80) + '...');
      
      // Create Audio element with CORS support
      const audio = new Audio();
      audio.crossOrigin = 'anonymous';
      audio.src = state.audioUrl;
      audio.preload = 'auto';
      
      // Store audio reference for playback control
      audioRef.current = audio;
      
      wavesurferRef.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: deck === 'A' ? '#00d4ff' : '#ff6b35',
        progressColor: deck === 'A' ? '#00a3cc' : '#cc5429',
        cursorColor: '#ffffff',
        cursorWidth: 2,
        barWidth: 2,
        barGap: 1,
        barRadius: 2,
        height: 45,
        normalize: true,
        media: audio, // Pass pre-configured audio element for v7
        mediaControls: false,
        interact: true,
        hideScrollbar: true
      });
    } else {
      // Create WaveSurfer without audio (will load later)
      wavesurferRef.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: deck === 'A' ? '#00d4ff' : '#ff6b35',
        progressColor: deck === 'A' ? '#00a3cc' : '#cc5429',
        cursorColor: '#ffffff',
        cursorWidth: 2,
        barWidth: 2,
        barGap: 1,
        barRadius: 2,
        height: 45,
        normalize: true,
        backend: 'MediaElement',
        mediaControls: false,
        interact: true,
        hideScrollbar: true
      });
    }
    
    wavesurferRef.current.on('ready', () => {
      console.log(`Deck ${deck}: WaveSurfer ready!`);
      setIsWaveformReady(true);
      const duration = wavesurferRef.current.getDuration();
      console.log(`Deck ${deck}: Duration: ${duration}s`);
      onPositionUpdate(0, duration);
      // Ensure volume is set when ready
      wavesurferRef.current.setVolume(state.volume);
    });
    
    wavesurferRef.current.on('audioprocess', (time) => {
      if (!isScrubbing) {
        const duration = wavesurferRef.current.getDuration();
        onPositionUpdate(time, duration);
        // Update rotation based on playback (3 rotations per second at normal speed)
        setRotation(prev => prev + 2);
      }
    });
    
    wavesurferRef.current.on('seek', (progress) => {
      const duration = wavesurferRef.current.getDuration();
      onPositionUpdate(progress * duration, duration);
    });
    
    wavesurferRef.current.on('finish', () => {
      onPlay();
    });
    
    // Handle errors
    wavesurferRef.current.on('error', (error) => {
      console.error(`Deck ${deck}: WaveSurfer error:`, error);
    });
    
    // Handle loading events
    wavesurferRef.current.on('loading', (percent) => {
      console.log(`Deck ${deck}: Loading ${percent}%`);
    });
    
    // Handle decode event (waveform data ready)
    wavesurferRef.current.on('decode', (duration) => {
      console.log(`Deck ${deck}: Audio decoded, duration: ${duration}s`);
    });
    
    return () => {
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, [audioContextStarted, deck, state.track, state.audioUrl]); // Include audioUrl in deps
  
  // Handle audioUrl changes when WaveSurfer already exists (for subsequent track loads)
  useEffect(() => {
    // Skip if no wavesurfer or no new audioUrl, or if this is the initial load (handled above)
    if (!wavesurferRef.current || !state.audioUrl) return;
    
    // Check if audio is already loaded with this URL
    if (audioRef.current && audioRef.current.src === state.audioUrl) return;
    
    console.log(`Deck ${deck}: Loading new audio URL:`, state.audioUrl.substring(0, 80) + '...');
    setIsWaveformReady(false);
    
    // Create new Audio element with CORS support
    const audio = new Audio();
    audio.crossOrigin = 'anonymous';
    audio.src = state.audioUrl;
    audio.preload = 'auto';
    audioRef.current = audio;
    
    // Load the audio into existing wavesurfer
    try {
      wavesurferRef.current.load(state.audioUrl);
    } catch (error) {
      console.error(`Deck ${deck}: Failed to load audio:`, error);
    }
  }, [state.audioUrl, deck]);
  
  // Handle play/pause
  useEffect(() => {
    if (!wavesurferRef.current || !isWaveformReady) return;
    
    try {
      if (state.isPlaying && !isScrubbing) {
        wavesurferRef.current.play();
      } else {
        wavesurferRef.current.pause();
      }
    } catch (error) {
      console.error('Play/pause error:', error);
    }
  }, [state.isPlaying, isWaveformReady, isScrubbing]);
  
  // Handle volume - includes master volume and crossfader
  useEffect(() => {
    if (wavesurferRef.current && isWaveformReady) {
      try {
        const effectiveVolume = calculateEffectiveVolume();
        wavesurferRef.current.setVolume(effectiveVolume);
      } catch (error) {
        console.error('Volume error:', error);
      }
    }
  }, [state.volume, masterVolume, crossfaderValue, isWaveformReady, calculateEffectiveVolume]);
  
  // Handle pitch (playback rate)
  useEffect(() => {
    if (wavesurferRef.current && isWaveformReady) {
      const rate = 1 + (state.pitch / 100); // Convert ±8% to rate
      console.log(`Deck ${deck}: Setting playback rate to ${rate.toFixed(3)} (pitch: ${state.pitch.toFixed(2)}%)`);
      wavesurferRef.current.setPlaybackRate(rate);
    }
  }, [state.pitch, isWaveformReady, deck]);
  
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Cue functions - Professional DJ behavior with toggle:
  // - First click: Sets cue point at current position (if no cue) OR enables cue mode
  // - When cue is active and playing: Audio will loop back to cue point when released
  // - Click again to disable cue mode
  const handleCueClick = useCallback(() => {
    if (!wavesurferRef.current || !isWaveformReady) return;
    
    if (cueActive) {
      // Toggle off cue mode
      setCueActive(false);
    } else {
      // Set cue point at current position and activate
      onCuePoint(state.position);
      setCueActive(true);
      
      // If playing, jump to cue and pause
      if (state.isPlaying) {
        if (state.position >= 0 && state.duration > 0) {
          wavesurferRef.current.seekTo(state.position / state.duration);
        }
        onPlay(); // Toggle to pause
      }
    }
  }, [cueActive, state.isPlaying, state.position, state.duration, isWaveformReady, onPlay, onCuePoint]);
  
  const jumpToCue = useCallback(() => {
    if (wavesurferRef.current && state.cuePoint >= 0 && state.duration > 0) {
      wavesurferRef.current.seekTo(state.cuePoint / state.duration);
    }
  }, [state.cuePoint, state.duration]);
  
  const setCueHere = useCallback(() => {
    onCuePoint(state.position);
  }, [onCuePoint, state.position]);
  
  // Jog wheel scrubbing - Fixed to properly traverse the waveform
  const handleScrub = useCallback((timeDelta) => {
    if (!wavesurferRef.current || !isWaveformReady || state.duration === 0) return;
    
    setIsScrubbing(true);
    
    // Use state.position instead of getCurrentTime() for more reliable scrubbing
    // This ensures we always have the latest position
    const currentTime = state.position;
    const newTime = Math.max(0, Math.min(state.duration, currentTime + timeDelta));
    const seekPosition = newTime / state.duration;
    
    // Seek to the new position
    wavesurferRef.current.seekTo(seekPosition);
    
    // Update the position state directly for smoother scrubbing feedback
    onPositionUpdate(newTime, state.duration);
    
    // Update rotation based on scrub
    setRotation(prev => prev + (timeDelta * 36)); // 36 degrees per second
  }, [isWaveformReady, state.duration, state.position, onPositionUpdate]);
  
  const handleScrubEnd = useCallback(() => {
    setIsScrubbing(false);
    // Resume playback if it was playing before
    if (state.isPlaying && wavesurferRef.current) {
      wavesurferRef.current.play();
    }
  }, [state.isPlaying]);
  
  const deckColor = deck === 'A' ? '#00d4ff' : '#ff6b35';
  
  return (
    <div className={`deck-core deck-${deck.toLowerCase()}`} data-testid={`deck-${deck.toLowerCase()}`}>
      {/* Top Row: Waveform Area */}
      <div className="deck-waveform-area">
        {state.isLoading ? (
          <div className="waveform-loading">
            <Loader2 size={16} className="spinning" />
            <span>Loading...</span>
          </div>
        ) : state.track ? (
          <>
            {/* Condensed single-line header: Title - Artist | BPM */}
            <div className="waveform-track-header-condensed">
              <div className="track-info-line">
                <span className="track-title-inline">{state.track.title || state.track.name}</span>
                <span className="track-separator">—</span>
                <span className="track-artist-inline">{state.track.artist || state.track.artists?.join(', ') || 'Unknown'}</span>
              </div>
              {state.bpm && (
                <span className="bpm-badge-inline" style={{ color: deckColor }}>
                  {Math.round(state.bpm)} BPM
                </span>
              )}
            </div>
            {/* Waveform with time display */}
            <div className="waveform-row">
              <div ref={waveformRef} className="waveform-container" />
              <div className="time-display">
                <span>{formatTime(state.position)}</span>
                <span className="time-sep">/</span>
                <span>{formatTime(state.duration)}</span>
              </div>
            </div>
            {!state.audioUrl && (
              <div className="no-audio-warning">No audio preview available</div>
            )}
          </>
        ) : (
          <button className="load-track-btn" onClick={onLoadTrack}>
            <Upload size={16} />
            <span>Load Track</span>
            <span className="load-hint">No track loaded</span>
          </button>
        )}
      </div>
      
      {/* Main Deck Area: Pitch Slider | Jog Wheel + Transport | Volume Knob */}
      <div className="deck-main-area">
        {/* Pitch Slider - Left side */}
        <PitchSlider 
          value={state.pitch || 0}
          onChange={onPitchChange}
          deckColor={deckColor}
        />
        
        {/* Center: Jog Wheel + Transport */}
        <div className="deck-center">
          <JogWheel 
            deck={deck}
            isPlaying={state.isPlaying}
            rotation={rotation}
            albumArt={state.track?.album_art || state.track?.artwork_url}
            position={state.position}
            duration={state.duration}
            onScrub={handleScrub}
            onScrubEnd={handleScrubEnd}
            size={150}
          />
          
          {/* Transport Controls - Below jog wheel */}
          <div className="deck-transport">
            {/* CUE button - Toggle behavior */}
            <button 
              className={`transport-btn cue-btn ${cueActive ? 'active' : ''} ${state.cuePoint > 0 ? 'has-cue' : ''}`}
              onClick={handleCueClick}
              disabled={!state.track}
              title={cueActive ? "Click to disable CUE" : "Set cue point here"}
              data-testid={`cue-btn-${deck.toLowerCase()}`}
            >
              <Disc size={12} />
              <span className="btn-label">CUE</span>
            </button>
            
            {/* Play/Pause button */}
            <button 
              className={`transport-btn play-btn ${state.isPlaying ? 'playing' : ''}`}
              onClick={onPlay}
              disabled={!state.track || !state.audioUrl}
              style={{ 
                background: state.isPlaying ? deckColor : undefined,
                borderColor: deckColor
              }}
            >
              {state.isPlaying ? <Pause size={14} /> : <Play size={14} />}
            </button>
            
            {/* Eject button */}
            {state.track && (
              <button className="transport-btn eject-btn" onClick={onEject} title="Eject track">
                <LogOut size={12} />
              </button>
            )}
          </div>
        </div>
        
        {/* Volume Knob - Right side (bottom corner) */}
        <VolumeKnob 
          value={state.volume}
          onChange={onVolumeChange}
          deckColor={deckColor}
        />
      </div>
    </div>
  );
};

export default DeckCore;
